import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import CancelIcon from "@mui/icons-material/Cancel";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelOutlinedIcon from "@mui/icons-material/HighlightOff";
import MicIcon from "@mui/icons-material/Mic";
import axios from "axios";
import moment from "moment";
import { LoadingButton } from "@material-ui/lab";
import bulkDeliveryCenterPendingCount from "../../../Services/bulkDeliveryCenterPendingCount";
import { getDecryptDataFromLocal } from "../../../utils/encrypt";
import { SoftphoneButton } from "../../../ui-component/SoftphoneCall";

// Only Bhavna and Kajal can mark an agent/manager reply valid/invalid — same two
// numbers as RAD_EDIT_USERS in DeliveryCenterOrders.js, kept as its own constant here
// since it's a distinct permission (see MANAGER_REPLY_VALIDITY_MARK_USERS on the backend).
const REPLY_VALIDITY_MARK_USERS = ["7011576531", "7048952168"];

// Who can flag a message/chat as "recording needed" — a DEDICATED, narrower
// allowlist (not SCM_TEAM_PHONE_NUMBERS), matching RECORDING_REQUEST_USERS on
// the backend (orderService.js) — as of 2026-09-16 effectively the whole SCM
// team by name: Bhawna, Harshit, Amrita Saha, Vardan + Sheetal, Kajal, Amisha.
// Flagging locks the order's agent/TL dashboard until the agent calls this
// number and it connects (see RtoRiskLockPopup's recording_required branch).
const RECORDING_REQUEST_USERS = ["7011576531", "9811068834", "9625766488", "8882083014", "9315481432", "7048952168", "9310278575", "8076818878"];

// Manual force-unlock (Shailendra, 2026-09-18) — Harshit + Bhawna Gautam only, deliberately
// a SEPARATE narrower allowlist from RECORDING_REQUEST_USERS above (flagging a recording as
// needed vs. force-clearing that flag are different permissions). Mirrors
// RECORDING_REQUIRED_MANUAL_UNLOCK_USERS on the backend (orderService.js) — keep in sync.
const RECORDING_REQUIRED_MANUAL_UNLOCK_USERS = ["9811068834", "7011576531"];

// Any file type is accepted, classified into 4 buckets so the chat can render
// each one appropriately (inline image / playable video / playable audio /
// generic download link) — this must match the backend's bodyType enum.
function bodyTypeForFile(file) {
  if (!file || !file.type) return "file";
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";
  return "file";
}

// Same env var/trim as MyChats.js — the call-history endpoint lives on the AI service.
const AI_PATH = (process.env.REACT_APP_AI_PATH || "").trim();

// Call chip visuals — same as MyChats.js's callChipVisual (2026-10-05: show the
// buyer number's call history + recordings inline in this chat, like My Chats).
function callChipVisual(status) {
  if (status === "connected") return { icon: "📞", color: "#137333", label: "Connected" };
  if (status === "missed") return { icon: "📵", color: "#d32f2f", label: "Missed call" };
  return { icon: "📞", color: "#b26a00", label: "Dialed — no answer" };
}
function formatCallDuration(sec) {
  const t = Math.max(0, Math.round(Number(sec) || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
}

// Inline call-history chip — connected Phonomenal calls only (see
// /voice/phone-call-timeline). Outbound on our side (right), inbound on the
// buyer's side (left), play button when a recording exists.
function CallChip({ call, isPlaying, onTogglePlay }) {
  const visual = callChipVisual(call.status);
  const onOurSide = call.direction !== "inbound";
  return (
    <Box sx={{ display: "flex", justifyContent: onOurSide ? "flex-end" : "flex-start", mb: 1 }}>
      <Box
        sx={{
          backgroundColor: "#eef2f7",
          border: "1px solid #dbe3ea",
          color: "#374151",
          px: 1.5,
          py: 0.6,
          borderRadius: 3,
          fontSize: 12,
          fontWeight: 600,
          maxWidth: "75%",
          boxShadow: "0 1px 0.5px rgba(0,0,0,0.08)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
          <span>{visual.icon}</span>
          <span style={{ color: visual.color }}>{visual.label}</span>
          {call.talktime > 0 && <span>· {formatCallDuration(call.talktime)}</span>}
          {call.agentName && <span style={{ color: "#6b7280" }}>· {call.agentName}</span>}
          <span style={{ color: "#9e9e9e" }}>· {moment(call.at).format("hh:mm A")}</span>
          {call.recordingUrl && (
            <IconButton
              size="small"
              onClick={onTogglePlay}
              sx={{ p: 0.25 }}
              title={isPlaying ? "Hide recording" : "Play recording"}
            >
              <span style={{ fontSize: 13 }}>{isPlaying ? "⏸" : "▶"}</span>
            </IconButton>
          )}
        </Box>
        {isPlaying && call.recordingUrl && (
          <audio
            controls
            autoPlay
            src={call.recordingUrl}
            style={{ width: "100%", height: 28, marginTop: 4 }}
            onEnded={onTogglePlay}
          />
        )}
      </Box>
    </Box>
  );
}

function formatDaySeparator(date) {
  const m = moment(date);
  if (m.isSame(moment(), "day")) return "Today";
  if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return m.format("DD MMM YYYY");
}

function HeaderPendingBadge() {
  const [n, setN] = useState(bulkDeliveryCenterPendingCount.current);
  useEffect(() => bulkDeliveryCenterPendingCount.subscribe(setN), []);
  if (!n || n <= 0) return null;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 22,
        height: 22,
        padding: "0 6px",
        borderRadius: 11,
        background: "#d32f2f",
        color: "#fff",
        fontSize: 12,
        fontWeight: 800,
        lineHeight: 1,
      }}
      title="Pending unread chats"
    >
      {n > 99 ? "99+" : n}
    </Box>
  );
}

// Recording-request status trail (see recordingStatus in getDeliveryCenterOrderChat) — so
// whoever requested it, or the agent, can tell where it stands without guessing from the
// chat alone: requested (no call yet) -> dialed_not_connected (agent tried, didn't connect,
// will need to call again — see the recording_required lock in RtoRiskLockPopup) -> connected
// (resolved; the recording itself lands as a SYSTEM message in the thread below).
// The call that satisfies this lock is the agent calling the BUYER (recordingStatus.
// callTargetPhone) — SCM's own number (requiredByPhone) is attribution only ("flagged by").
// Label is a function of (callTargetPhone, requiredByPhone), spelling out "Call the buyer"
// explicitly so it's never confused with SCM's number.
const RECORDING_STATUS_META = {
  requested: (target, scm) => `Call the buyer (${target}) to unlock — flagged by SCM (${scm})`,
  dialed_not_connected: (target) => `Didn't connect — call the buyer (${target}) again within 30 minutes or this re-locks`,
  connected: () => "Recording received",
};
const RECORDING_STATUS_COLOR = {
  requested: { color: "#c62828", bg: "#fdecea" },
  dialed_not_connected: { color: "#e65100", bg: "#fff4e5" },
  connected: { color: "#2e7d32", bg: "#e8f5e9" },
};

function RecordingStatusBadge({ recordingStatus }) {
  const labelFn = recordingStatus && RECORDING_STATUS_META[recordingStatus.state];
  const colors = recordingStatus && RECORDING_STATUS_COLOR[recordingStatus.state];
  if (!labelFn || !colors) return null;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.5, px: 1, py: 0.25, borderRadius: 1, backgroundColor: colors.bg }}>
      <MicIcon sx={{ fontSize: 14, color: colors.color }} />
      <Typography variant="caption" sx={{ color: colors.color, fontWeight: 700 }}>
        {labelFn(recordingStatus.callTargetPhone, recordingStatus.requiredByPhone)}
      </Typography>
    </Box>
  );
}

function formatAddress(address) {
  if (!address) return "";
  const parts = [address.address, address.landmark, address.city, address.state, address.pincode].filter(Boolean);
  return parts.join(", ");
}

function MessageBubble({ message, isRight, canMarkValidity, onMarkValidity, marking, canRequestRecording, onRequestRecording, requestingRecording }) {
  const readers = (message.readBy || []).filter((r) => r.name && r.name !== message.senderName);
  const showValidityControls = canMarkValidity && message.senderRole === "AGENT_MANAGER" && !message.legacy;
  const showRecordingControl = canRequestRecording && !message.legacy;
  return (
    <Box sx={{ display: "flex", justifyContent: isRight ? "flex-end" : "flex-start", mb: 1.5 }}>
      <Box
        sx={{
          maxWidth: "70%",
          backgroundColor: isRight ? "#dcf8c6" : "#ffffff",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          p: 1,
          position: "relative",
          boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
        }}
      >
        <Typography variant="caption" sx={{ display: "block", textAlign: "right", fontWeight: 600, color: "#555" }}>
          {message.senderName || (message.senderRole === "SCM" ? "SCM" : "Agent/Manager")}
        </Typography>
        {message.bodyType === "image" && message.mediaUrl && (
          <Box sx={{ mt: 0.5, mb: message.text ? 0.5 : 0 }}>
            <img
              src={message.mediaUrl}
              alt="attachment"
              style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 6, cursor: "pointer" }}
              onClick={() => window.open(message.mediaUrl, "_blank")}
            />
          </Box>
        )}
        {message.bodyType === "video" && message.mediaUrl && (
          <Box sx={{ mt: 0.5, mb: message.text ? 0.5 : 0 }}>
            <video src={message.mediaUrl} controls style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 6 }} />
          </Box>
        )}
        {message.bodyType === "audio" && message.mediaUrl && (
          <Box sx={{ mt: 0.5, mb: message.text ? 0.5 : 0 }}>
            <audio src={message.mediaUrl} controls style={{ maxWidth: "100%" }} />
          </Box>
        )}
        {message.bodyType === "file" && message.mediaUrl && (
          <Box
            component="a"
            href={message.mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              mt: 0.5,
              mb: message.text ? 0.5 : 0,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              p: 0.75,
              backgroundColor: "#f0f0f0",
              borderRadius: 1,
              textDecoration: "none",
              color: "#075e54",
            }}
          >
            <InsertDriveFileIcon fontSize="small" />
            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
              {message.mediaName || "Attachment"}
            </Typography>
          </Box>
        )}
        {message.text && <Typography variant="body2">{message.text}</Typography>}
        <Typography variant="caption" sx={{ display: "block", color: "#888", mt: 0.5 }}>
          {moment(message.createdAt).format("hh:mm A")}
        </Typography>
        {!message.legacy && (
          <Typography variant="caption" sx={{ display: "block", color: "#888" }}>
            {readers.length ? `Read by ${readers.map((r) => r.name).join(", ")}` : "Sent"}
          </Typography>
        )}
        {message.replyValidity && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontWeight: 700,
              color: message.replyValidity === "valid" ? "#2e7d32" : "#c62828",
            }}
          >
            Marked {message.replyValidity === "valid" ? "valid" : "invalid"} by {message.replyValidityMarkedByName || "SCM"}
          </Typography>
        )}
        {message.recordingRequested && (
          <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: "#c62828" }}>
            Recording requested by {message.recordingRequestedByName || "SCM"}
          </Typography>
        )}
        {showRecordingControl && (
          <IconButton
            size="small"
            disabled={requestingRecording}
            title="Request call recording for this message"
            onClick={() => onRequestRecording(message._id)}
            sx={{ color: message.recordingRequested ? "#c62828" : "#9e9e9e", p: 0.25, mt: 0.5 }}
          >
            <MicIcon fontSize="small" />
          </IconButton>
        )}
        {showValidityControls && (
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
            <IconButton
              size="small"
              disabled={marking}
              title="Mark reply valid"
              onClick={() => onMarkValidity(message._id, "valid")}
              sx={{ color: message.replyValidity === "valid" ? "#2e7d32" : "#9e9e9e", p: 0.25 }}
            >
              <CheckCircleIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={marking}
              title="Mark reply invalid"
              onClick={() => onMarkValidity(message._id, "invalid")}
              sx={{ color: message.replyValidity === "invalid" ? "#c62828" : "#9e9e9e", p: 0.25 }}
            >
              <CancelOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function DeliveryCenterChatDrawer({ open, orderId, onClose, zIndex }) {
  const [orderInfo, setOrderInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachedPreview, setAttachedPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const [requestingRecordingId, setRequestingRecordingId] = useState(null);
  const [unlockingRecording, setUnlockingRecording] = useState(false);
  const [calls, setCalls] = useState([]);
  const [playingCallKey, setPlayingCallKey] = useState(null);
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);
  const canMarkValidity = REPLY_VALIDITY_MARK_USERS.includes(getDecryptDataFromLocal("phoneNumber"));
  const canRequestRecording = RECORDING_REQUEST_USERS.includes(getDecryptDataFromLocal("phoneNumber"));
  const canUnlockRecording = RECORDING_REQUIRED_MANUAL_UNLOCK_USERS.includes(getDecryptDataFromLocal("phoneNumber"));
  // While SCM is waiting on the agent's call (recordingStatus.state === "requested"), the
  // agent/TL side can't send messages — matches "agent must call before agent can chat" (the
  // same lock RtoRiskLockPopup shows for the dashboard). SCM (the allowlisted requester) can
  // still message. Once the agent has dialled — even if the call didn't connect —
  // recordingStatus flips to "dialed_not_connected"/"connected" and the composer reopens,
  // mirroring the dashboard's own 30-min-grace-then-relock cycle.
  const recordingLockState = orderInfo?.recordingStatus?.state;
  // Optimistic unlock — flips the instant the unlock Call button's dial succeeds (before any
  // webhook/poll round-trip), same as RtoRiskLockPopup's justDialedOrderIds. Reset whenever the
  // order changes so it doesn't leak across chats.
  const [justDialedRecording, setJustDialedRecording] = useState(false);
  useEffect(() => { setJustDialedRecording(false); }, [orderId]);
  const composerBlockedByRecording = recordingLockState === "requested" && !canRequestRecording && !justDialedRecording;

  const loadChat = () => {
    if (!orderId) return;
    setLoading(true);
    axios
      .post(`${process.env.REACT_APP_PATH_BULK}/sales/getDeliveryCenterOrderChat`, { orderId })
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          setOrderInfo(res.data.data?.order || null);
          setMessages(res.data.data?.messages || []);
          // Opening the chat marks messages read on the backend, so the
          // sidebar pending badge should drop immediately, not after the poll.
          bulkDeliveryCenterPendingCount.refresh();
        } else {
          alert(res.data.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        alert(err?.response?.data?.message || "something went wrong");
      });
  };

  useEffect(() => {
    if (open && orderId) {
      loadChat();
    } else {
      setOrderInfo(null);
      setMessages([]);
      setText("");
      setAttachedFile(null);
      setAttachedPreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId]);

  // Call history for the buyer's number(s) — fetched once per order, not on
  // every chat poll (recordings don't change that fast).
  const callPhones = [orderInfo?.phoneNumber, orderInfo?.address?.phoneNumber].filter(Boolean).join(",");
  useEffect(() => {
    setCalls([]);
    setPlayingCallKey(null);
    if (!open || !callPhones) return;
    let cancelled = false;
    axios
      .get(`${AI_PATH}/api/ai/voice/phone-call-timeline`, { params: { phones: callPhones } })
      .then((res) => { if (!cancelled && res.data?.success) setCalls(res.data.calls || []); })
      .catch((err) => console.warn("[DeliveryCenterChat] call history failed:", err.message));
    return () => { cancelled = true; };
  }, [open, orderId, callPhones]);

  // Chat messages + calls, one chronological thread (like My Chats).
  const timelineItems = [
    ...messages.map((m) => ({ kind: "msg", at: m.createdAt, data: m, key: m._id || `${m.senderId}-${m.createdAt}` })),
    ...calls.map((c, i) => ({ kind: "call", at: c.at, data: c, key: `call-${i}-${c.at}` })),
    ...(orderInfo?.orderCreatedAt ? [{ kind: "order", at: orderInfo.orderCreatedAt, key: "order-placed" }] : []),
  ].sort((a, b) => new Date(a.at) - new Date(b.at));

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, calls]);

  // While a recording request is outstanding (state 'requested' or 'dialed_not_connected'),
  // poll so the status badge, the composer lock, and the auto-attached recording (once
  // connected) all update without the viewer having to close/reopen the drawer. 5s while
  // still "requested" (composer is blocked — want the unlock to land fast right after a
  // dial, matching RtoRiskLockPopup's own fast poll), 30s once "dialed_not_connected" (composer
  // already open, this is just refreshing the badge/recording).
  const recordingState = orderInfo?.recordingStatus?.state;
  useEffect(() => {
    if (!open || !orderId) return undefined;
    if (recordingState !== "requested" && recordingState !== "dialed_not_connected") return undefined;
    const ms = recordingState === "requested" ? 5000 : 30000;
    const t = setInterval(loadChat, ms);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId, recordingState]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachedFile(file);
    // Only images/video get a real preview thumbnail — everything else (pdf,
    // audio, docs, ...) just shows a filename chip in the composer.
    setAttachedPreview(file.type.startsWith("image/") ? URL.createObjectURL(file) : null);
  };

  const removeAttachment = () => {
    setAttachedFile(null);
    setAttachedPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSend = async () => {
    if (!text.trim() && !attachedFile) return;
    setSending(true);
    try {
      let mediaUrl = "";
      let bodyType = "text";
      let mediaName = "";
      if (attachedFile) {
        bodyType = bodyTypeForFile(attachedFile);
        mediaName = attachedFile.name;
        const form = new FormData();
        form.append("image", attachedFile);
        const uploadRes = await axios.post(
          `${process.env.REACT_APP_PATH_BULK}/sales/uploadDeliveryCenterChatImage`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (!uploadRes.data.success) {
          alert(uploadRes.data.message || "attachment upload failed");
          setSending(false);
          return;
        }
        mediaUrl = uploadRes.data.data?.url || uploadRes.data.message?.url || "";
      }
      const res = await axios.post(`${process.env.REACT_APP_PATH_BULK}/sales/sendDeliveryCenterChatMessage`, {
        orderId,
        text: text.trim(),
        mediaUrl,
        bodyType,
        mediaName,
      });
      setSending(false);
      if (res.data.success) {
        setText("");
        removeAttachment();
        loadChat();
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      setSending(false);
      alert(err?.response?.data?.message || "something went wrong");
    }
  };

  const handleMarkValidity = async (messageId, validity) => {
    setMarkingId(messageId);
    try {
      const res = await axios.post(`${process.env.REACT_APP_PATH_BULK}/sales/markDeliveryCenterChatReplyValidity`, {
        orderId,
        messageId,
        validity,
      });
      if (res.data.success) {
        loadChat();
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      alert(err?.response?.data?.message || "something went wrong");
    } finally {
      setMarkingId(null);
    }
  };

  const handleRequestRecording = async (messageId) => {
    setRequestingRecordingId(messageId || "chat");
    try {
      const res = await axios.post(`${process.env.REACT_APP_PATH_BULK}/sales/markDeliveryCenterChatRecordingRequired`, {
        orderId,
        messageId: messageId || undefined,
      });
      if (res.data.success) {
        loadChat();
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      alert(err?.response?.data?.message || "something went wrong");
    } finally {
      setRequestingRecordingId(null);
    }
  };

  // Force-unlocks without waiting for the agent's call to connect — the order's agent AND
  // their immediate senior both unlock, per the backend's existing Path 3 scoping.
  const handleUnlockRecording = async () => {
    setUnlockingRecording(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_PATH_BULK}/sales/unlockDeliveryCenterRecordingRequired`, {
        orderId,
      });
      if (res.data.success) {
        loadChat();
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      alert(err?.response?.data?.message || "something went wrong");
    } finally {
      setUnlockingRecording(false);
    }
  };

  let lastDay = null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Default MUI drawer z-index (1200) sits BELOW the RtoRiskLockPopup lock overlay
      // (1300) — callers opening this drawer from inside that overlay (e.g. "View Chat" on
      // the recording-required lock screen) must pass a higher zIndex or the drawer renders
      // invisibly behind the black backdrop.
      sx={zIndex ? { zIndex } : undefined}
      PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, ...(zIndex ? { zIndex: zIndex + 1 } : {}) } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", backgroundColor: "#f5f5f5" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={600}>
              {orderInfo?.customerName || "Order Chat"}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <HeaderPendingBadge />
              {orderInfo?.phoneNumber && (
                <SoftphoneButton
                  number={orderInfo.phoneNumber}
                  title="Call customer"
                  color="#075e54"
                  // If a recording is currently required on this order, tag the call with the
                  // same custom_id the lock is watching for — this IS the recording-required
                  // call (the agent calls the buyer either way), so this one button satisfies
                  // both "call the customer" and "unlock the recording lock" at once.
                  customId={orderInfo?.recordingStatus?.customId}
                  onCallPlaced={() => setJustDialedRecording(true)}
                />
              )}
              {canRequestRecording && (
                <IconButton
                  size="small"
                  disabled={requestingRecordingId === "chat"}
                  title="Request call recording for this chat"
                  onClick={() => handleRequestRecording(null)}
                  sx={{ color: "#c62828" }}
                >
                  <MicIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Invoice: {orderInfo?.invoiceNumber || "-"} | Phone: {orderInfo?.phoneNumber || "-"}
          </Typography>
          {orderInfo?.address && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {formatAddress(orderInfo.address)}
            </Typography>
          )}
          <RecordingStatusBadge recordingStatus={orderInfo?.recordingStatus} />
          {canUnlockRecording && ["requested", "dialed_not_connected"].includes(orderInfo?.recordingStatus?.state) && (
            <LoadingButton
              size="small"
              variant="outlined"
              color="error"
              loading={unlockingRecording}
              onClick={handleUnlockRecording}
              sx={{ mt: 0.5 }}
              title="Force-unlock without waiting for the agent's call — unlocks for both the agent and their immediate senior"
            >
              Unlock Now
            </LoadingButton>
          )}
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", p: 2, backgroundColor: "#ece5dd" }}>
          {timelineItems.map((item) => {
            const day = moment(item.at).format("YYYY-MM-DD");
            const showSeparator = day !== lastDay;
            lastDay = day;
            const separator = showSeparator && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 1.5 }}>
                <Box sx={{ backgroundColor: "#fff", borderRadius: 2, px: 1.5, py: 0.3 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatDaySeparator(item.at)}
                  </Typography>
                </Box>
              </Box>
            );
            if (item.kind === "order") {
              return (
                <React.Fragment key={item.key}>
                  {separator}
                  <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
                    <Box sx={{ backgroundColor: "#fff", borderRadius: 2, px: 1.5, py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Order placed on {moment(item.at).format("DD MMM YYYY, hh:mm A")}
                      </Typography>
                    </Box>
                  </Box>
                </React.Fragment>
              );
            }
            if (item.kind === "call") {
              const isPlaying = playingCallKey === item.key;
              return (
                <React.Fragment key={item.key}>
                  {separator}
                  <CallChip
                    call={item.data}
                    isPlaying={isPlaying}
                    onTogglePlay={() => setPlayingCallKey(isPlaying ? null : item.key)}
                  />
                </React.Fragment>
              );
            }
            const message = item.data;
            const isRight = message.senderRole === "AGENT_MANAGER";
            return (
              <React.Fragment key={item.key}>
                {separator}
                <MessageBubble
                  message={message}
                  isRight={isRight}
                  canMarkValidity={canMarkValidity}
                  onMarkValidity={handleMarkValidity}
                  marking={markingId === message._id}
                  canRequestRecording={canRequestRecording}
                  onRequestRecording={handleRequestRecording}
                  requestingRecording={requestingRecordingId === message._id}
                />
              </React.Fragment>
            );
          })}
          {!loading && timelineItems.length === 0 && (
            <Typography variant="body2" color="text.secondary" align="center">
              No messages yet.
            </Typography>
          )}
          <div ref={bottomRef} />
        </Box>

        <Box sx={{ p: 1.5, borderTop: "1px solid #e0e0e0" }}>
          {composerBlockedByRecording ? (
            <Stack spacing={1} alignItems="center" sx={{ py: 1 }}>
              <Typography variant="caption" sx={{ color: "#c62828", fontWeight: 700, textAlign: "center" }}>
                SCM ({orderInfo?.recordingStatus?.requiredByPhone}) needs a recording of your call with the buyer
                ({orderInfo?.recordingStatus?.callTargetPhone}) before you can reply here.
              </Typography>
              <SoftphoneButton
                number={orderInfo?.recordingStatus?.callTargetPhone}
                customId={orderInfo?.recordingStatus?.customId}
                title={`Call buyer ${orderInfo?.recordingStatus?.callTargetPhone}`}
                color="#c62828"
                onCallPlaced={() => setJustDialedRecording(true)}
                onDispositionClosed={loadChat}
              />
            </Stack>
          ) : (
          <>
          {attachedFile && (
            <Box sx={{ position: "relative", display: "inline-block", mb: 1 }}>
              {attachedPreview ? (
                <img src={attachedPreview} alt="preview" style={{ maxHeight: 100, borderRadius: 6 }} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    p: 1,
                    pr: 3,
                    backgroundColor: "#f0f0f0",
                    borderRadius: 1,
                    maxWidth: 260,
                  }}
                >
                  <InsertDriveFileIcon fontSize="small" />
                  <Typography variant="caption" noWrap>
                    {attachedFile.name}
                  </Typography>
                </Box>
              )}
              <IconButton size="small" onClick={removeAttachment} sx={{ position: "absolute", top: -10, right: -10 }}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <input
              type="file"
              accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
            <IconButton onClick={() => fileInputRef.current?.click()}>
              <AttachFileIcon />
            </IconButton>
            <TextField
              size="small"
              fullWidth
              placeholder="Type a message"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <LoadingButton variant="contained" loading={sending} onClick={handleSend}>
              <SendIcon fontSize="small" />
            </LoadingButton>
          </Stack>
          </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export default DeliveryCenterChatDrawer;
